import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Eye, X, Monitor, Smartphone, Tablet,
  ZoomIn, ZoomOut, Send,
  Type, Image, Palette, Grid3X3,
  Layers, Loader2, Plus, Trash2,
} from 'lucide-react';
import { useSite, useLead } from '@/hooks/useFirestore';
import { cloudFunctions } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { OutreachModal } from '@/components/site/OutreachModal';
import { markSiteSent } from '@/lib/outreach';
import { renderTemplate, type BusinessInfo, type CustomTheme } from '@shared/siteRenderer';
import { getDefaultEnabledFeatures } from '@/lib/features';
import { TEMPLATES } from '@/lib/templates';

// Rose color constant to match app theme
const ROSE = '#B08080';
const ROSE_LIGHT = '#C4A0A0';

// Types
type SiteContent = {
  headline: string;
  aboutText: string;
  services: Array<{ name: string; description: string; icon?: string; price?: string }>;
  testimonials: Array<{ name: string; role?: string; content: string; rating?: number }>;
  seo: { title: string; description: string };
  faq?: Array<{ question: string; answer: string }>;
  videoUrl?: string;
};

type BusinessData = {
  phone?: string | null;
  address?: string | null;
  rating?: number | null;
  reviewCount?: number;
  businessHours?: string[];
  photos?: string[];
  googleMapsUrl?: string | null;
  googleReviews?: Array<{ authorName: string; rating: number; text: string; relativeTime?: string }>;
};

type SiteModel = {
  content: SiteContent;
  selectedFeatures: string[];
  customTheme: CustomTheme;
  templateType: string;
  businessData: BusinessData;
};

type ViewMode = 'desktop' | 'tablet' | 'mobile';
type SidebarPanel = 'design' | 'elements' | 'text' | 'images' | 'sections' | null;

const DEFAULT_THEME: Required<CustomTheme> = {
  primaryColor: '#667eea',
  accentColor: '#764ba2',
  bgColor: '#ffffff',
  textColor: '#1f2937',
  bodyTextColor: '#6b7280',
  headingFont: 'Inter',
  bodyFont: 'Inter',
};

const normalizeTheme = (theme: CustomTheme): Required<CustomTheme> => ({
  primaryColor: theme.primaryColor ?? DEFAULT_THEME.primaryColor,
  accentColor: theme.accentColor ?? DEFAULT_THEME.accentColor,
  bgColor: theme.bgColor ?? DEFAULT_THEME.bgColor,
  textColor: theme.textColor ?? DEFAULT_THEME.textColor,
  bodyTextColor: theme.bodyTextColor ?? DEFAULT_THEME.bodyTextColor,
  headingFont: theme.headingFont ?? DEFAULT_THEME.headingFont,
  bodyFont: theme.bodyFont ?? DEFAULT_THEME.bodyFont,
});

const normalizeBusinessData = (data: BusinessData): BusinessInfo => ({
  phone: data.phone ?? undefined,
  address: data.address ?? undefined,
  rating: data.rating ?? undefined,
  reviewCount: data.reviewCount,
  businessHours: data.businessHours,
  photos: data.photos,
  googleMapsUrl: data.googleMapsUrl ?? undefined,
});

// Convert HSL string "H S% L%" to hex color
const hslToHex = (hsl: string): string => {
  const match = hsl.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%?\s+(\d+(?:\.\d+)?)%?$/);
  if (!match) {
    if (hsl.startsWith('#')) return hsl;
    return '#1f2937';
  }

  const h = parseFloat(match[1]) / 360;
  const s = parseFloat(match[2]) / 100;
  const l = parseFloat(match[3]) / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Sidebar Button - Rose themed
function SidebarButton({
  icon: Icon,
  label,
  active,
  onClick
}: {
  icon: React.ElementType;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 w-full text-left text-sm transition-all border-l-2"
      style={{
        borderColor: active ? ROSE : 'transparent',
        background: active ? `${ROSE}15` : 'transparent',
        color: active ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
      }}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );
}

// Delete Confirmation Modal
function DeleteConfirmModal({
  type,
  onConfirm,
  onCancel
}: {
  type: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/80">
      <div className="bg-[#161616] rounded-lg p-6 max-w-sm w-full mx-4" style={{ border: `1px solid ${ROSE}40` }}>
        <h3 className="text-lg font-medium mb-2" style={{ color: ROSE_LIGHT }}>
          Delete {type}?
        </h3>
        <p className="text-sm mb-6" style={{ color: `${ROSE_LIGHT}99` }}>
          This action cannot be undone. Are you sure you want to continue?
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded transition-colors"
            style={{ border: `1px solid ${ROSE}40`, color: `${ROSE_LIGHT}BB` }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500/20 border border-red-400/50 text-red-400 hover:bg-red-500/30 transition-colors text-sm rounded"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Loading Overlay for theme changes
function ThemeUpdateOverlay({ progress }: { progress: number }) {
  return (
    <div className="absolute inset-0 z-20 bg-black/60 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-[#161616] rounded-lg p-6 text-center" style={{ border: `1px solid ${ROSE}40` }}>
        <Loader2 size={32} className="animate-spin mx-auto mb-3" style={{ color: ROSE_LIGHT }} />
        <div className="text-sm mb-3" style={{ color: ROSE_LIGHT }}>
          Applying changes...
        </div>
        <div className="w-48 h-2 rounded-full overflow-hidden" style={{ background: `${ROSE}30` }}>
          <div
            className="h-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%`, background: ROSE }}
          />
        </div>
      </div>
    </div>
  );
}

// Animated particles background with rose color
function ParticlesBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Static subtle dots pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at center, ${ROSE}20 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />
      {/* Floating particles */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full animate-pulse"
          style={{
            background: `${ROSE}50`,
            left: `${10 + (i * 7) % 80}%`,
            top: `${15 + (i * 11) % 70}%`,
            animationDelay: `${i * 0.3}s`,
            animationDuration: `${2 + (i % 3)}s`,
          }}
        />
      ))}
    </div>
  );
}

// Device mockup frame with rose accents
function DeviceMockup({
  viewMode,
  children
}: {
  viewMode: ViewMode;
  children: React.ReactNode;
}) {
  if (viewMode === 'desktop') {
    return (
      <div className="flex flex-col" style={{ width: '1200px' }}>
        {/* Browser chrome */}
        <div
          className="bg-[#1e1e1e] rounded-t-lg px-4 py-2.5 flex items-center gap-3"
          style={{ border: `1px solid ${ROSE}30`, borderBottom: 'none' }}
        >
          {/* Traffic lights */}
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <div className="w-3 h-3 rounded-full bg-green-500/70" />
          </div>
          {/* URL bar */}
          <div
            className="flex-1 bg-[#0f0f0f] rounded px-3 py-1 text-xs font-mono"
            style={{ color: `${ROSE_LIGHT}80` }}
          >
            yoursite.romulus.ai
          </div>
        </div>
        {/* Browser content */}
        <div
          className="rounded-b-lg overflow-hidden bg-white"
          style={{ border: `1px solid ${ROSE}30`, borderTop: 'none' }}
        >
          {children}
        </div>
      </div>
    );
  }

  if (viewMode === 'tablet') {
    return (
      <div className="flex flex-col">
        {/* Tablet frame */}
        <div
          className="bg-[#1e1e1e] rounded-[24px] p-3"
          style={{ border: `1px solid ${ROSE}30` }}
        >
          {/* Camera notch */}
          <div className="flex justify-center mb-2">
            <div className="w-2 h-2 rounded-full" style={{ background: `${ROSE}50` }} />
          </div>
          {/* Screen */}
          <div className="rounded-lg overflow-hidden bg-white" style={{ width: '768px' }}>
            {children}
          </div>
          {/* Home indicator */}
          <div className="flex justify-center mt-2">
            <div className="w-16 h-1 rounded-full" style={{ background: `${ROSE}30` }} />
          </div>
        </div>
      </div>
    );
  }

  // Mobile
  return (
    <div className="flex flex-col">
      {/* Phone frame */}
      <div
        className="bg-[#1e1e1e] rounded-[36px] p-2"
        style={{ border: `1px solid ${ROSE}30` }}
      >
        {/* Notch */}
        <div className="flex justify-center mb-1">
          <div className="w-20 h-5 bg-[#0f0f0f] rounded-full flex items-center justify-center">
            <div className="w-2 h-2 rounded-full" style={{ background: `${ROSE}50` }} />
          </div>
        </div>
        {/* Screen */}
        <div className="rounded-[28px] overflow-hidden bg-white" style={{ width: '375px' }}>
          {children}
        </div>
        {/* Home indicator */}
        <div className="flex justify-center mt-2">
          <div className="w-24 h-1 rounded-full" style={{ background: `${ROSE}30` }} />
        </div>
      </div>
    </div>
  );
}

// Base scale factors - these make the "100%" zoom show the content at a comfortable size
const VIEW_SCALE_FACTORS: Record<ViewMode, number> = {
  desktop: 0.7,   // Desktop at 100% zoom appears at 70% actual size
  tablet: 0.57,   // Tablet at 100% zoom appears at 57% actual size
  mobile: 0.8,    // Mobile at 100% zoom appears at 80% actual size
};

// Zoom controls component for bottom-left
function ZoomControls({
  zoom,
  onZoomIn,
  onZoomOut
}: {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}) {
  return (
    <div
      className="absolute bottom-6 left-6 z-20 flex items-center gap-1 rounded-lg px-2 py-1"
      style={{ background: '#1a1a1a', border: `1px solid ${ROSE}30` }}
    >
      <button
        onClick={onZoomOut}
        className="p-1.5 transition-colors rounded"
        style={{ color: `${ROSE_LIGHT}99` }}
        title="Zoom out"
      >
        <ZoomOut size={16} />
      </button>
      <span className="text-sm w-14 text-center font-mono" style={{ color: ROSE_LIGHT }}>
        {zoom}%
      </span>
      <button
        onClick={onZoomIn}
        className="p-1.5 transition-colors rounded"
        style={{ color: `${ROSE_LIGHT}99` }}
        title="Zoom in"
      >
        <ZoomIn size={16} />
      </button>
    </div>
  );
}

// Main Editor Component
export default function CanvaSiteEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { data: site, isLoading, error } = useSite(siteId);
  const { userData } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>('');
  const themeDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const hasStripeConnect = userData?.stripeConnectStatus === 'active';
  const hasConnectAccount = !!userData?.stripeConnectAccountId;

  // Opportunistic sync: if the Stripe account.updated webhook missed an event,
  // reconcile status whenever the editor loads for a user who has a Connect
  // account ID but isn't marked active yet. Keeps the Send-to-Business button
  // from sticking on "Set Up Payouts First" after onboarding completes.
  useEffect(() => {
    if (!hasConnectAccount || hasStripeConnect) return;
    cloudFunctions.getConnectStatus({}).catch(() => { /* best-effort */ });
  }, [hasConnectAccount, hasStripeConnect]);

  // Outreach modal state
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [markedAsSent, setMarkedAsSent] = useState(false);

  // Derive sent state from both Firestore data and local state (useDocument doesn't live-update)
  const isSent = markedAsSent || site?.status === 'sent';

  // Fetch lead for outreach modal
  const { data: outreachLead, isLoading: isLoadingLead } = useLead(showOutreachModal ? site?.leadId : undefined);

  // UI State - Default zoom at 100% (base scale factors handle comfortable sizing)
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [zoom, setZoom] = useState(100);
  const [sidebarPanel, setSidebarPanel] = useState<SidebarPanel>('design');
  const [designTab, setDesignTab] = useState<'colors' | 'fonts'>('colors');
  const [saving, setSaving] = useState(false);
  const [undoStack, setUndoStack] = useState<SiteContent[]>([]);
  const [redoStack, setRedoStack] = useState<SiteContent[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSavingOverlay, setShowSavingOverlay] = useState(false);
  const [exitAfterSave, setExitAfterSave] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Gates the iframe render: stays false until we've copied real content/theme
  // in from site data. Otherwise the iframe mounts with the default-theme
  // render (wrong background/fonts), and only a device toggle (which remounts
  // the mockup container) forces a clean reload — that's the "switch tablet
  // and back" workaround the user kept hitting.
  const [contentReady, setContentReady] = useState(false);
  const [themeUpdating, setThemeUpdating] = useState(false);
  const [themeProgress, setThemeProgress] = useState(0);

  // Element management state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ type: string; index?: number } | null>(null);

  // Site Data State
  const [siteModel, setSiteModel] = useState<SiteModel>({
    content: {
      headline: '',
      aboutText: '',
      services: [],
      testimonials: [],
      seo: { title: '', description: '' },
      faq: [],
      videoUrl: '',
    },
    selectedFeatures: getDefaultEnabledFeatures(),
    businessData: {},
    customTheme: {
      primaryColor: '#667eea',
      accentColor: '#764ba2',
      bgColor: '#ffffff',
      textColor: '#1f2937',
      bodyTextColor: '#6b7280',
    },
    templateType: 'modern-gradient',
  });

  // Stable content for rendering - this is what we display in forms AND preview
  const [stableContent, setStableContent] = useState<SiteContent>({
    headline: '',
    aboutText: '',
    services: [],
    testimonials: [],
    seo: { title: '', description: '' },
    faq: [],
    videoUrl: '',
  });

  // Stable theme - only updates after debounce
  const [stableTheme, setStableTheme] = useState<CustomTheme>(DEFAULT_THEME);

  const siteModelRef = useRef(siteModel);
  // Preserved iframe scroll position across srcDoc reloads. srcDoc changes
  // reload the iframe document (unavoidable browser behavior), which by
  // default slams the user back to the top and flashes white. We restore
  // scrollY in the onLoad handler so edits feel stable.
  const iframeScrollRef = useRef(0);

  const updateSiteModel = useCallback((updater: (prev: SiteModel) => SiteModel) => {
    setSiteModel(prev => {
      const next = updater(prev);
      siteModelRef.current = next;
      return next;
    });
  }, []);

  const { selectedFeatures, customTheme, templateType, businessData } = siteModel;

  useEffect(() => {
    siteModelRef.current = siteModel;
  }, [siteModel]);

  // Restore scroll + track new scrolls on each srcDoc reload. srcDoc changes
  // create a fresh contentWindow, so listeners attached to the previous one
  // are gone - re-attach here.
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const win = iframe?.contentWindow;
    if (!win) return;
    try {
      win.scrollTo(0, iframeScrollRef.current);
      win.addEventListener(
        'scroll',
        () => {
          iframeScrollRef.current = win.scrollY || 0;
        },
        { passive: true },
      );
    } catch {
      // Cross-origin edge cases - swallow, scroll preservation is best-effort.
    }
  }, []);

  // Load site data
  useEffect(() => {
    if (!site) return;

    const loadedContent: SiteContent = {
      headline: site.content?.headline || '',
      aboutText: site.content?.aboutText || '',
      services: site.content?.services || [],
      testimonials: site.content?.testimonials || [],
      seo: site.content?.seo || { title: '', description: '' },
      faq: site.content?.faq || [],
      videoUrl: site.content?.videoUrl || '',
    };

    const loadedSelectedFeatures =
      site.selectedFeatures && site.selectedFeatures.length > 0
        ? site.selectedFeatures
        : getDefaultEnabledFeatures();

    const loadedBusinessData: BusinessData = site.businessData
      ? {
          phone: site.businessData.phone,
          address: site.businessData.address,
          rating: site.businessData.rating,
          reviewCount: site.businessData.reviewCount,
          businessHours: site.businessData.businessHours,
          photos: site.businessData.photos,
          googleMapsUrl: site.businessData.googleMapsUrl,
          googleReviews: site.businessData.googleReviews,
        }
      : {};

    const resolvedTemplateType = site.templateType || siteModelRef.current.templateType;
    const template = TEMPLATES.find(t => t.id === resolvedTemplateType);

    // Get template's native theme as fallback
    const templateFallback = template ? {
      primaryColor: hslToHex(template.theme.primary),
      accentColor: hslToHex(template.theme.accent),
      bgColor: hslToHex(template.theme.background),
      textColor: hslToHex(template.theme.text),
      bodyTextColor: hslToHex(template.theme.bodyText),
      headingFont: template.typography.fontHeading || 'Inter',
      bodyFont: template.typography.fontBody || 'Inter',
    } : DEFAULT_THEME;

    const loadedCustomTheme: CustomTheme = site.customTheme
      ? {
          primaryColor: site.customTheme.primaryColor || templateFallback.primaryColor,
          accentColor: site.customTheme.accentColor || templateFallback.accentColor,
          bgColor: site.customTheme.bgColor || templateFallback.bgColor,
          textColor: site.customTheme.textColor || templateFallback.textColor,
          bodyTextColor: site.customTheme.bodyTextColor || templateFallback.bodyTextColor,
          headingFont: site.customTheme.headingFont || templateFallback.headingFont,
          bodyFont: site.customTheme.bodyFont || templateFallback.bodyFont,
        }
      : templateFallback;

    updateSiteModel(prev => ({
      ...prev,
      content: loadedContent,
      selectedFeatures: loadedSelectedFeatures,
      businessData: loadedBusinessData,
      customTheme: loadedCustomTheme,
      templateType: resolvedTemplateType,
    }));
    setStableContent(loadedContent);
    setStableTheme(loadedCustomTheme);
    setContentReady(true);
  }, [site, updateSiteModel]);

  const updateContent = useCallback((updates: Partial<SiteContent>) => {
    setUndoStack(prev => [...prev, siteModelRef.current.content]);
    setRedoStack([]);

    const newContent = { ...siteModelRef.current.content, ...updates };
    updateSiteModel(prev => ({ ...prev, content: newContent }));
    setStableContent(newContent);
    setHasUnsavedChanges(true);
  }, [updateSiteModel]);

  // Update theme with loading overlay
  const updateTheme = useCallback((key: string, value: string) => {
    // Update the model immediately (for color picker to stay in sync)
    updateSiteModel(prev => ({
      ...prev,
      customTheme: { ...prev.customTheme, [key]: value },
    }));
    setHasUnsavedChanges(true);

    // Clear any existing debounce
    if (themeDebounceRef.current) {
      clearTimeout(themeDebounceRef.current);
    }

    // Show loading overlay
    setThemeUpdating(true);
    setThemeProgress(0);

    // Animate progress
    const progressInterval = setInterval(() => {
      setThemeProgress(prev => Math.min(prev + 15, 90));
    }, 100);

    // Debounce the actual theme update
    themeDebounceRef.current = setTimeout(() => {
      clearInterval(progressInterval);
      setThemeProgress(100);

      // Apply the theme change
      setStableTheme(siteModelRef.current.customTheme);

      // Hide overlay after a brief moment
      setTimeout(() => {
        setThemeUpdating(false);
        setThemeProgress(0);
      }, 200);
    }, 600);
  }, [updateSiteModel]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, siteModelRef.current.content]);
    updateSiteModel(prev => ({ ...prev, content: previous }));
    setStableContent(previous);
    setHasUnsavedChanges(true);
  }, [undoStack, updateSiteModel]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setUndoStack(prev => [...prev, siteModelRef.current.content]);
    updateSiteModel(prev => ({ ...prev, content: next }));
    setStableContent(next);
    setHasUnsavedChanges(true);
  }, [redoStack, updateSiteModel]);

  const performSave = useCallback(async (): Promise<boolean> => {
    if (!siteId || saving) return false;

    const { content, selectedFeatures, customTheme, templateType, businessData } = siteModelRef.current;
    const currentState = JSON.stringify({ content, selectedFeatures, customTheme, templateType, businessData });
    if (currentState === lastSavedRef.current) return true;

    setSaving(true);
    try {
      const normalizedTheme = normalizeTheme(customTheme);
      await cloudFunctions.updateSiteContent({
        siteId,
        content,
        selectedFeatures,
        customTheme: normalizedTheme,
        templateType,
        businessData: { photos: businessData.photos },
      });

      lastSavedRef.current = currentState;
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save changes');
      return false;
    } finally {
      setSaving(false);
    }
  }, [siteId, saving]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, performSave]);

  const handleSave = async () => {
    const saved = await performSave();
    if (saved) {
      toast.success('Changes saved');
    }
  };

  const previewHtml = useMemo(() => {
    if (!site) return '';

    const normalizedTheme = normalizeTheme(stableTheme);

    // Build Google Fonts URL for the selected fonts so the browser can load them
    const fontFamilies = new Set([normalizedTheme.headingFont, normalizedTheme.bodyFont]);
    const fontsParam = Array.from(fontFamilies)
      .map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700;800;900`)
      .join('&');
    const googleFontsLink = `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?${fontsParam}&display=swap" rel="stylesheet">`;

    // Inject CSS overrides for custom theme colors/fonts so the editor color
    // pickers actually affect the preview regardless of the template's defaults.
    const themeOverrides = `
      ${googleFontsLink}
      <style>
        :root {
          --primary: ${normalizedTheme.primaryColor} !important;
          --accent: ${normalizedTheme.accentColor} !important;
          --bg: ${normalizedTheme.bgColor} !important;
          --text: ${normalizedTheme.textColor} !important;
          --text-muted: ${normalizedTheme.bodyTextColor} !important;
          --text-light: ${normalizedTheme.bodyTextColor} !important;
          --font-heading: '${normalizedTheme.headingFont}', sans-serif !important;
          --font-body: '${normalizedTheme.bodyFont}', sans-serif !important;
        }
      </style>
    `;

    const previewScript = `
      <base target="_self">
      ${themeOverrides}
      <script>
        document.addEventListener('click', function(e) {
          var link = e.target.closest('a');
          if (!link) return;
          var href = link.getAttribute('href');
          if (!href) return;
          if (href.startsWith('#')) {
            e.preventDefault();
            if (href === '#') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
            var target = document.querySelector(href);
            if (target) target.scrollIntoView({ behavior: 'smooth' });
            return;
          }
          e.preventDefault();
        });
      <\/script>
    `;

    // Always use the template renderer in the editor so content edits
    // are reflected in real-time. The generatedHtml is used for the
    // published preview page, not for the editing experience.
    const normalizedBusinessData = normalizeBusinessData(businessData);

    let html = renderTemplate(templateType, {
      businessName: site.name,
      headline: stableContent.headline,
      aboutText: stableContent.aboutText,
      services: stableContent.services,
      testimonials: stableContent.testimonials,
      seo: stableContent.seo,
      businessData: normalizedBusinessData,
      customTheme: normalizedTheme,
    }, selectedFeatures);

    if (!html || !html.includes('</body>')) {
      return '<html><body><h1 style="color:red;padding:20px;">Error: Failed to generate preview</h1></body></html>';
    }
    html = html.replace('</body>', `${previewScript}</body>`);
    return html;
  }, [stableContent, site, templateType, selectedFeatures, businessData, stableTheme]);

  const handleExit = useCallback(async () => {
    if (hasUnsavedChanges || saving) {
      setShowSavingOverlay(true);
      setExitAfterSave(true);
      if (!saving) {
        const saved = await performSave();
        if (!saved) {
          setShowSavingOverlay(false);
          setExitAfterSave(false);
        }
      }
      return;
    }
    navigate('/dashboard/sites');
  }, [hasUnsavedChanges, saving, navigate, performSave]);

  useEffect(() => {
    if (exitAfterSave && !saving) {
      setShowSavingOverlay(false);
      setExitAfterSave(false);
      navigate('/dashboard/sites');
    }
  }, [exitAfterSave, saving, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const siteName = site?.name ?? 'Site Editor';
  const showEditor = !!site;
  const showLoading = isLoading && !site;
  const showNotFound = !isLoading && !site && !error;

  const confirmDelete = useCallback(() => {
    if (!showDeleteConfirm) return;

    if (showDeleteConfirm.type === 'service' && showDeleteConfirm.index !== undefined) {
      const newServices = stableContent.services.filter((_, i) => i !== showDeleteConfirm.index);
      updateContent({ services: newServices });
      toast.success('Service deleted');
    } else if (showDeleteConfirm.type === 'testimonial' && showDeleteConfirm.index !== undefined) {
      const newTestimonials = stableContent.testimonials.filter((_, i) => i !== showDeleteConfirm.index);
      updateContent({ testimonials: newTestimonials });
      toast.success('Testimonial deleted');
    }

    setShowDeleteConfirm(null);
  }, [showDeleteConfirm, stableContent.services, stableContent.testimonials, updateContent]);

  const handleZoomIn = () => setZoom(z => Math.min(150, z + 10));
  const handleZoomOut = () => setZoom(z => Math.max(20, z - 10));

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#0a0a0a] flex flex-col">
      {/* Subtle scanline overlay */}
      <div className="pointer-events-none fixed inset-0 z-50 bg-[repeating-linear-gradient(0deg,rgba(0,0,0,0.02),rgba(0,0,0,0.02)_1px,transparent_1px,transparent_2px)] opacity-30" />

      {showDeleteConfirm && (
        <DeleteConfirmModal
          type={showDeleteConfirm.type}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}

      {/* Top Toolbar */}
      <header
        className="h-14 bg-[#111] flex items-center justify-between px-4 flex-shrink-0"
        style={{ borderBottom: `1px solid ${ROSE}30` }}
      >
        {/* Left section */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleExit}
            className="p-2 rounded transition-colors"
            style={{ color: `${ROSE_LIGHT}99` }}
            title="Exit editor"
          >
            <X size={20} />
          </button>
          <div className="h-6 w-px" style={{ background: `${ROSE}30` }} />
          <span className="text-sm" style={{ color: ROSE_LIGHT }}>{siteName}</span>
          <div className="flex items-center gap-2 text-sm">
            {saving ? (
              <span className="text-yellow-400 flex items-center gap-1">
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </span>
            ) : hasUnsavedChanges ? (
              <span className="text-yellow-400/80">Unsaved changes</span>
            ) : (
              <span style={{ color: `${ROSE_LIGHT}80` }}>Saved</span>
            )}
          </div>
        </div>

        {/* Center section - Device view toggle */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <div
            className="flex items-center rounded overflow-hidden"
            style={{ background: `${ROSE}10`, border: `1px solid ${ROSE}30` }}
          >
            <button
              onClick={() => setViewMode('desktop')}
              className="px-3 py-2 transition-colors flex items-center gap-2"
              style={{
                background: viewMode === 'desktop' ? `${ROSE}30` : 'transparent',
                color: viewMode === 'desktop' ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
              }}
              title="Desktop view"
            >
              <Monitor size={16} />
              <span className="text-sm">Desktop</span>
            </button>
            <button
              onClick={() => setViewMode('tablet')}
              className="px-3 py-2 transition-colors flex items-center gap-2"
              style={{
                background: viewMode === 'tablet' ? `${ROSE}30` : 'transparent',
                color: viewMode === 'tablet' ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
              }}
              title="Tablet view"
            >
              <Tablet size={16} />
              <span className="text-sm">Tablet</span>
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className="px-3 py-2 transition-colors flex items-center gap-2"
              style={{
                background: viewMode === 'mobile' ? `${ROSE}30` : 'transparent',
                color: viewMode === 'mobile' ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
              }}
              title="Mobile view"
            >
              <Smartphone size={16} />
              <span className="text-sm">Mobile</span>
            </button>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          <button
            disabled={previewLoading}
            onClick={async () => {
              if (site?.previewToken) {
                window.open(`/p/${site.previewToken}`, '_blank');
                return;
              }
              setPreviewLoading(true);
              try {
                const result = await cloudFunctions.getPreviewShareLink({ siteId: siteId! });
                window.open(`/p/${result.token}`, '_blank');
              } finally {
                setPreviewLoading(false);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 transition-colors rounded text-sm"
            style={{ border: `1px solid ${ROSE}40`, color: `${ROSE_LIGHT}BB` }}
          >
            {previewLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Eye size={14} />
            )}
            Preview
          </button>

          <button
            onClick={() => {
              if (!hasStripeConnect) {
                toast.info('Two minutes to finish setup. Then you can send.');
                navigate(`/onboarding?return=${encodeURIComponent(window.location.pathname)}`);
                return;
              }
              // Save first, then open outreach
              if (hasUnsavedChanges) {
                performSave().then(() => setShowOutreachModal(true));
              } else {
                setShowOutreachModal(true);
              }
            }}
            title={!hasStripeConnect ? 'Set up Stripe Connect first' : (isSent ? 'Send updated preview to business' : 'Send preview to business')}
            className="flex items-center gap-2 px-4 py-2 transition-colors rounded text-sm font-medium"
            style={{
              background: hasStripeConnect ? `${ROSE}20` : 'rgba(255,255,255,0.05)',
              border: `1px solid ${hasStripeConnect ? `${ROSE}60` : 'rgba(255,255,255,0.1)'}`,
              color: hasStripeConnect ? ROSE_LIGHT : '#666',
              opacity: hasStripeConnect ? 1 : 0.7,
            }}
          >
            <Send size={14} />
            {!hasStripeConnect ? 'Set Up Payouts First' : isSent ? 'Send Updated Site' : 'Send to Business'}
          </button>
        </div>
      </header>

      {showSavingOverlay && (
        <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#161616] rounded-lg px-8 py-6" style={{ border: `1px solid ${ROSE}40` }}>
            <div className="flex items-center gap-3" style={{ color: ROSE_LIGHT }}>
              <Loader2 size={24} className="animate-spin" />
              <span className="text-lg">Saving your changes...</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Design Panel */}
        <div
          className="w-80 bg-[#0d0d0d] flex flex-col"
          style={{ borderRight: `1px solid ${ROSE}30` }}
        >
          {/* Panel Header */}
          <div className="px-4 py-4" style={{ borderBottom: `1px solid ${ROSE}20` }}>
            <h2 className="text-sm font-medium" style={{ color: ROSE_LIGHT }}>
              Edit Your Site
            </h2>
          </div>

          {/* Navigation */}
          <div className="py-2" style={{ borderBottom: `1px solid ${ROSE}20` }}>
            <SidebarButton
              icon={Palette}
              label="Design"
              active={sidebarPanel === 'design'}
              onClick={() => setSidebarPanel(sidebarPanel === 'design' ? null : 'design')}
            />
            <SidebarButton
              icon={Type}
              label="Content"
              active={sidebarPanel === 'text'}
              onClick={() => setSidebarPanel(sidebarPanel === 'text' ? null : 'text')}
            />
            <SidebarButton
              icon={Layers}
              label="Elements"
              active={sidebarPanel === 'elements'}
              onClick={() => setSidebarPanel(sidebarPanel === 'elements' ? null : 'elements')}
            />
            <SidebarButton
              icon={Image}
              label="Images"
              active={sidebarPanel === 'images'}
              onClick={() => setSidebarPanel(sidebarPanel === 'images' ? null : 'images')}
            />
            <SidebarButton
              icon={Grid3X3}
              label="Sections"
              active={sidebarPanel === 'sections'}
              onClick={() => setSidebarPanel(sidebarPanel === 'sections' ? null : 'sections')}
            />
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Design Panel */}
            {sidebarPanel === 'design' && (
              <>
                <h3 className="text-sm font-medium mb-4" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Theme Settings
                </h3>

                {(() => {
                  const currentTemplate = TEMPLATES.find(t => t.id === templateType);
                  if (currentTemplate && !currentTemplate.themeEditable) {
                    return (
                      <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: `${ROSE}15`, border: `1px solid ${ROSE}30`, color: `${ROSE_LIGHT}CC` }}>
                        This template uses a signature color scheme that cannot be customized.
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Tab Switcher */}
                <div
                  className="flex rounded mb-4"
                  style={{ background: `${ROSE}10`, border: `1px solid ${ROSE}30` }}
                >
                  <button
                    onClick={() => setDesignTab('colors')}
                    className="flex-1 px-4 py-2 text-sm transition-colors rounded-l"
                    style={{
                      background: designTab === 'colors' ? `${ROSE}30` : 'transparent',
                      color: designTab === 'colors' ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
                    }}
                  >
                    Colors
                  </button>
                  <button
                    onClick={() => setDesignTab('fonts')}
                    className="flex-1 px-4 py-2 text-sm transition-colors rounded-r"
                    style={{
                      background: designTab === 'fonts' ? `${ROSE}30` : 'transparent',
                      color: designTab === 'fonts' ? ROSE_LIGHT : `${ROSE_LIGHT}99`,
                    }}
                  >
                    Fonts
                  </button>
                </div>

                {designTab === 'colors' && (
                  <div className="space-y-4">
                    {[
                      { label: 'Primary Color', key: 'primaryColor', value: customTheme.primaryColor },
                      { label: 'Accent Color', key: 'accentColor', value: customTheme.accentColor },
                      { label: 'Background', key: 'bgColor', value: customTheme.bgColor || '#ffffff' },
                      { label: 'Heading Color', key: 'textColor', value: customTheme.textColor || '#1f2937' },
                      { label: 'Body Text Color', key: 'bodyTextColor', value: customTheme.bodyTextColor || '#6b7280' },
                    ].map((color) => (
                      <div key={color.key}>
                        <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                          {color.label}
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={color.value}
                            onChange={(e) => updateTheme(color.key, e.target.value)}
                            className="w-10 h-10 cursor-pointer rounded bg-transparent"
                            style={{ border: `1px solid ${ROSE}40` }}
                          />
                          <input
                            type="text"
                            value={color.value}
                            onChange={(e) => updateTheme(color.key, e.target.value)}
                            className="flex-1 bg-[#151515] rounded px-3 py-2 text-sm focus:outline-none font-mono"
                            style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {designTab === 'fonts' && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                        Heading Font
                      </label>
                      <select
                        value={customTheme.headingFont || 'Inter'}
                        onChange={(e) => updateTheme('headingFont', e.target.value)}
                        className="w-full bg-[#151515] rounded px-3 py-2.5 text-sm focus:outline-none"
                        style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                      >
                        {[
                          // Modern & Clean
                          'Inter', 'Poppins', 'Outfit', 'DM Sans', 'Plus Jakarta Sans', 'Space Grotesk',
                          // Classic & Elegant
                          'Playfair Display', 'Merriweather', 'Cormorant Garamond', 'Libre Baskerville', 'Spectral',
                          // Bold & Display
                          'Bebas Neue', 'Anton', 'Archivo Black', 'Righteous', 'Montserrat', 'Teko',
                          // Creative & Decorative
                          'Pacifico', 'Caveat', 'Satisfy', 'Cookie', 'Bangers', 'Bungee', 'Baloo 2', 'Fredoka One',
                          // Luxury & Serif
                          'Cinzel', 'Italiana', 'Bodoni Moda', 'Tenor Sans', 'Cormorant', 'Marcellus',
                          // Tech & Mono
                          'Orbitron', 'Space Mono', 'JetBrains Mono', 'Share Tech Mono',
                          // Other
                          'Roboto', 'Lato', 'Quicksand', 'Karla', 'Comfortaa', 'Libre Franklin', 'Source Sans Pro',
                          'Noto Serif JP',
                        ].map(f => (
                          <option key={f} value={f} className="bg-black">{f}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                        Body Font
                      </label>
                      <select
                        value={customTheme.bodyFont || 'Inter'}
                        onChange={(e) => updateTheme('bodyFont', e.target.value)}
                        className="w-full bg-[#151515] rounded px-3 py-2.5 text-sm focus:outline-none"
                        style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                      >
                        {[
                          // Modern & Clean
                          'Inter', 'DM Sans', 'Poppins', 'Open Sans', 'Nunito', 'Plus Jakarta Sans',
                          // Classic & Serif
                          'Lora', 'Source Serif Pro', 'EB Garamond', 'Crimson Text', 'Spectral', 'Cormorant',
                          // Sans Serif
                          'Roboto', 'Lato', 'Montserrat', 'Quicksand', 'Karla', 'Archivo', 'Roboto Condensed',
                          'Raleway', 'Josefin Sans', 'Nunito Sans', 'Libre Franklin', 'Source Sans Pro',
                          // Mono
                          'IBM Plex Mono', 'Fira Code', 'Share Tech Mono', 'JetBrains Mono',
                          // Other
                          'Noto Sans JP',
                        ].map(f => (
                          <option key={f} value={f} className="bg-black">{f}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Text Content Panel */}
            {sidebarPanel === 'text' && (
              <>
                <h3 className="text-sm font-medium mb-4" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Edit Content
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Headline
                    </label>
                    <textarea
                      value={stableContent.headline}
                      onChange={(e) => updateContent({ headline: e.target.value })}
                      placeholder="Enter your main headline..."
                      className="w-full bg-[#151515] rounded px-3 py-2 text-sm resize-none focus:outline-none"
                      style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                      About Text
                    </label>
                    <textarea
                      value={stableContent.aboutText}
                      onChange={(e) => updateContent({ aboutText: e.target.value })}
                      placeholder="Tell visitors about your business..."
                      className="w-full bg-[#151515] rounded px-3 py-2 text-sm resize-none focus:outline-none"
                      style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Page Title (SEO)
                    </label>
                    <input
                      type="text"
                      value={stableContent.seo.title}
                      onChange={(e) => updateContent({ seo: { ...stableContent.seo, title: e.target.value } })}
                      placeholder="Page title for search engines..."
                      className="w-full bg-[#151515] rounded px-3 py-2 text-sm focus:outline-none"
                      style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                    />
                  </div>
                  <div>
                    <label className="text-sm mb-2 block" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Description (SEO)
                    </label>
                    <textarea
                      value={stableContent.seo.description}
                      onChange={(e) => updateContent({ seo: { ...stableContent.seo, description: e.target.value } })}
                      placeholder="Brief description for search results..."
                      className="w-full bg-[#151515] rounded px-3 py-2 text-sm resize-none focus:outline-none"
                      style={{ border: `1px solid ${ROSE}40`, color: ROSE_LIGHT }}
                      rows={3}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Images Panel */}
            {sidebarPanel === 'images' && (
              <>
                <h3 className="text-sm font-medium mb-4" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Site Images
                </h3>
                {businessData.photos && businessData.photos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {businessData.photos.map((photo, idx) => (
                      <div
                        key={idx}
                        className="aspect-square rounded overflow-hidden"
                        style={{ border: `1px solid ${ROSE}40` }}
                      >
                        <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: `${ROSE_LIGHT}80` }}>
                    No images available
                  </p>
                )}
              </>
            )}

            {/* Sections Panel - Full editing for services and testimonials */}
            {sidebarPanel === 'sections' && (
              <>
                <h3 className="text-sm font-medium mb-4" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Page Sections
                </h3>

                {/* Services Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Services ({stableContent.services.length})
                    </span>
                    <button
                      onClick={() => {
                        const newService = { name: 'New Service', description: 'Service description', icon: 'star' };
                        updateContent({ services: [...stableContent.services, newService] });
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                      style={{ background: `${ROSE}20`, color: ROSE_LIGHT }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {stableContent.services.map((service, idx) => (
                      <div key={idx} className="bg-[#151515] rounded overflow-hidden" style={{ border: `1px solid ${ROSE}30` }}>
                        <div className="px-3 py-2 flex items-center justify-between">
                          <input
                            type="text"
                            value={service.name}
                            onChange={(e) => {
                              const newServices = [...stableContent.services];
                              newServices[idx] = { ...newServices[idx], name: e.target.value };
                              updateContent({ services: newServices });
                            }}
                            className="flex-1 bg-transparent text-sm focus:outline-none"
                            style={{ color: ROSE_LIGHT }}
                          />
                          <button
                            onClick={() => {
                              const newServices = stableContent.services.filter((_, i) => i !== idx);
                              updateContent({ services: newServices });
                            }}
                            className="p-1 rounded hover:bg-red-500/20 transition-colors"
                            style={{ color: `${ROSE_LIGHT}80` }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="px-3 pb-2">
                          <textarea
                            value={service.description}
                            onChange={(e) => {
                              const newServices = [...stableContent.services];
                              newServices[idx] = { ...newServices[idx], description: e.target.value };
                              updateContent({ services: newServices });
                            }}
                            placeholder="Service description..."
                            className="w-full bg-[#0d0d0d] rounded px-2 py-1.5 text-xs resize-none focus:outline-none"
                            style={{ color: `${ROSE_LIGHT}99`, border: `1px solid ${ROSE}20` }}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                    {stableContent.services.length === 0 && (
                      <p className="text-xs py-2" style={{ color: `${ROSE_LIGHT}60` }}>
                        No services yet. Click "Add" to create one.
                      </p>
                    )}
                  </div>
                </div>

                {/* Testimonials Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Testimonials ({stableContent.testimonials.length})
                    </span>
                    <button
                      onClick={() => {
                        const newTestimonial = { name: 'Customer Name', content: 'Great service!', rating: 5 };
                        updateContent({ testimonials: [...stableContent.testimonials, newTestimonial] });
                      }}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                      style={{ background: `${ROSE}20`, color: ROSE_LIGHT }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {stableContent.testimonials.map((testimonial, idx) => (
                      <div key={idx} className="bg-[#151515] rounded overflow-hidden" style={{ border: `1px solid ${ROSE}30` }}>
                        <div className="px-3 py-2 flex items-center justify-between">
                          <input
                            type="text"
                            value={testimonial.name}
                            onChange={(e) => {
                              const newTestimonials = [...stableContent.testimonials];
                              newTestimonials[idx] = { ...newTestimonials[idx], name: e.target.value };
                              updateContent({ testimonials: newTestimonials });
                            }}
                            className="flex-1 bg-transparent text-sm focus:outline-none"
                            style={{ color: ROSE_LIGHT }}
                            placeholder="Customer name"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={testimonial.rating || 5}
                              onChange={(e) => {
                                const newTestimonials = [...stableContent.testimonials];
                                newTestimonials[idx] = { ...newTestimonials[idx], rating: parseInt(e.target.value) };
                                updateContent({ testimonials: newTestimonials });
                              }}
                              className="bg-[#0d0d0d] text-xs px-1 py-0.5 rounded"
                              style={{ color: ROSE_LIGHT, border: `1px solid ${ROSE}20` }}
                            >
                              {[5, 4, 3, 2, 1].map(r => (
                                <option key={r} value={r}>{r} ★</option>
                              ))}
                            </select>
                            <button
                              onClick={() => {
                                const newTestimonials = stableContent.testimonials.filter((_, i) => i !== idx);
                                updateContent({ testimonials: newTestimonials });
                              }}
                              className="p-1 rounded hover:bg-red-500/20 transition-colors"
                              style={{ color: `${ROSE_LIGHT}80` }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="px-3 pb-2">
                          <textarea
                            value={testimonial.content}
                            onChange={(e) => {
                              const newTestimonials = [...stableContent.testimonials];
                              newTestimonials[idx] = { ...newTestimonials[idx], content: e.target.value };
                              updateContent({ testimonials: newTestimonials });
                            }}
                            placeholder="What did they say?"
                            className="w-full bg-[#0d0d0d] rounded px-2 py-1.5 text-xs resize-none focus:outline-none"
                            style={{ color: `${ROSE_LIGHT}99`, border: `1px solid ${ROSE}20` }}
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                    {stableContent.testimonials.length === 0 && (
                      <p className="text-xs py-2" style={{ color: `${ROSE_LIGHT}60` }}>
                        No testimonials yet. Click "Add" to create one.
                      </p>
                    )}
                  </div>
                </div>

                {/* FAQ Section (if available) */}
                {stableContent.faq && stableContent.faq.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium" style={{ color: `${ROSE_LIGHT}BB` }}>
                        FAQ ({stableContent.faq.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {stableContent.faq.map((faq, idx) => (
                        <div key={idx} className="bg-[#151515] rounded p-3" style={{ border: `1px solid ${ROSE}30` }}>
                          <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => {
                              const newFaq = [...(stableContent.faq || [])];
                              newFaq[idx] = { ...newFaq[idx], question: e.target.value };
                              updateContent({ faq: newFaq });
                            }}
                            className="w-full bg-transparent text-sm font-medium focus:outline-none mb-2"
                            style={{ color: ROSE_LIGHT }}
                            placeholder="Question"
                          />
                          <textarea
                            value={faq.answer}
                            onChange={(e) => {
                              const newFaq = [...(stableContent.faq || [])];
                              newFaq[idx] = { ...newFaq[idx], answer: e.target.value };
                              updateContent({ faq: newFaq });
                            }}
                            placeholder="Answer"
                            className="w-full bg-[#0d0d0d] rounded px-2 py-1.5 text-xs resize-none focus:outline-none"
                            style={{ color: `${ROSE_LIGHT}99`, border: `1px solid ${ROSE}20` }}
                            rows={2}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Elements Panel */}
            {sidebarPanel === 'elements' && (
              <>
                <h3 className="text-sm font-medium mb-4" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Site Elements
                </h3>
                <div className="space-y-6">
                  <div>
                    <div className="text-sm mb-3" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Services ({stableContent.services.length})
                    </div>
                    {stableContent.services.length > 0 ? (
                      stableContent.services.map((service, idx) => (
                        <div
                          key={idx}
                          className="bg-[#151515] rounded px-3 py-2 mb-2 text-sm"
                          style={{ border: `1px solid ${ROSE}30`, color: `${ROSE_LIGHT}CC` }}
                        >
                          {service.name}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm" style={{ color: `${ROSE_LIGHT}60` }}>
                        No services added
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="text-sm mb-3" style={{ color: `${ROSE_LIGHT}BB` }}>
                      Testimonials ({stableContent.testimonials.length})
                    </div>
                    {stableContent.testimonials.length > 0 ? (
                      stableContent.testimonials.map((testimonial, idx) => (
                        <div
                          key={idx}
                          className="bg-[#151515] rounded px-3 py-2 mb-2 text-sm"
                          style={{ border: `1px solid ${ROSE}30`, color: `${ROSE_LIGHT}CC` }}
                        >
                          {testimonial.name}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm" style={{ color: `${ROSE_LIGHT}60` }}>
                        No testimonials added
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Status bar */}
          <div className="px-4 py-3 text-sm" style={{ borderTop: `1px solid ${ROSE}20`, color: `${ROSE_LIGHT}80` }}>
            Template: <span style={{ color: ROSE_LIGHT }}>{templateType}</span>
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 bg-[#080808] overflow-hidden flex flex-col relative">
          {/* Particles background */}
          <ParticlesBackground />

          {/* Theme update overlay */}
          {themeUpdating && <ThemeUpdateOverlay progress={themeProgress} />}

          {/* Zoom controls - bottom left */}
          {showEditor && (
            <ZoomControls zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          )}

          {showLoading && (
            <div className="flex-1 flex items-center justify-center relative z-10">
              <div className="text-center" style={{ color: ROSE_LIGHT }}>
                <Loader2 size={32} className="animate-spin mb-3 mx-auto" />
                <div className="text-lg">Loading your site...</div>
              </div>
            </div>
          )}

          {error && !showEditor && (
            <div className="flex-1 flex items-center justify-center relative z-10">
              <div className="text-center bg-[#161616] border border-red-400/30 rounded-lg p-8 max-w-md w-full">
                <div className="text-red-400 text-xl font-medium mb-2">Unable to load site</div>
                <p className="text-red-400/70 mb-6">
                  {error instanceof Error ? error.message : 'An unknown error occurred'}
                </p>
                <Button
                  onClick={() => navigate('/dashboard/sites')}
                  style={{ background: `${ROSE}20`, border: `1px solid ${ROSE}60`, color: ROSE_LIGHT }}
                >
                  Back to Sites
                </Button>
              </div>
            </div>
          )}

          {showNotFound && (
            <div className="flex-1 flex items-center justify-center relative z-10">
              <div className="text-center bg-[#161616] border border-yellow-400/30 rounded-lg p-8 max-w-md w-full">
                <div className="text-yellow-400 text-xl font-medium mb-2">Site Not Found</div>
                <p className="text-yellow-400/70 mb-6">
                  This site may have been deleted or you don&apos;t have access to it.
                </p>
                <Button
                  onClick={() => navigate('/dashboard/sites')}
                  style={{ background: `${ROSE}20`, border: `1px solid ${ROSE}60`, color: ROSE_LIGHT }}
                >
                  Back to Sites
                </Button>
              </div>
            </div>
          )}

          {showEditor && (
            <div className="flex-1 overflow-auto relative z-10">
              {/* Header */}
              <div className="text-center py-6">
                <h2 className="text-lg font-medium" style={{ color: `${ROSE_LIGHT}CC` }}>
                  Live Preview
                </h2>
                <p className="text-sm mt-1" style={{ color: `${ROSE_LIGHT}66` }}>
                  {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} View
                </p>
              </div>

              {/* Device mockup container */}
              <div className="flex justify-center pb-12 px-8">
                <div
                  style={{
                    transform: `scale(${(zoom / 100) * VIEW_SCALE_FACTORS[viewMode]})`,
                    transformOrigin: 'top center',
                  }}
                >
                  <DeviceMockup viewMode={viewMode}>
                    {contentReady && previewHtml ? (
                      <iframe
                        // Keyed on contentReady so the first "real" render
                        // remounts the iframe cleanly instead of diffing
                        // srcDoc from a stale empty/default value — that
                        // diff was the reason a tablet-toggle was needed to
                        // see the real site.
                        key={contentReady ? 'site-ready' : 'site-loading'}
                        ref={iframeRef}
                        srcDoc={previewHtml}
                        sandbox="allow-scripts"
                        onLoad={handleIframeLoad}
                        className="w-full border-0"
                        style={{
                          width: viewMode === 'desktop' ? '1200px' : viewMode === 'tablet' ? '768px' : '375px',
                          height: viewMode === 'mobile' ? '700px' : viewMode === 'tablet' ? '1024px' : '800px',
                          backgroundColor: stableTheme?.bgColor || '#ffffff',
                        }}
                        title="Site Preview"
                      />
                    ) : (
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: viewMode === 'desktop' ? '1200px' : viewMode === 'tablet' ? '768px' : '375px',
                          height: viewMode === 'mobile' ? '700px' : viewMode === 'tablet' ? '1024px' : '800px',
                          backgroundColor: stableTheme?.bgColor || '#0a0a0a',
                          color: stableTheme?.textColor || '#9ca3af',
                        }}
                      >
                        <div className="flex flex-col items-center gap-3 opacity-70">
                          <div
                            className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent"
                            aria-label="Loading preview"
                          />
                          <span className="text-sm">Loading preview…</span>
                        </div>
                      </div>
                    )}
                  </DeviceMockup>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Outreach Modal - portaled to body */}
      {showOutreachModal && site && createPortal(
        <OutreachModal
          site={{ ...site, id: siteId! }}
          lead={outreachLead || null}
          isLoadingLead={isLoadingLead}
          onClose={async (markAsSent) => {
            if (markAsSent) {
              try {
                await markSiteSent(siteId!, site.leadId);
                setMarkedAsSent(true);
                toast.success('Site marked as sent!');
              } catch (err) {
                console.error('Failed to update site status:', err);
                toast.error('Failed to mark site as sent');
              }
            }
            setShowOutreachModal(false);
          }}
        />,
        document.body
      )}
    </div>
  );
}

